import { Component, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { FormBuilder, Validators } from '@angular/forms';
import { IVisualizer, VISUALIZER } from '@jax-data-science/sci-image-visualizer';

import { MainFacade } from '../../../main.facade';
import { IFileInfo } from '../../../models/file-info';
import { RequestFactory } from '../../../models/request.factory';
import {
  ProcessType,
  ScriptEngine,
  ScriptSourceMode,
  StorageKey,
  ValidationType,
} from '../../../models/request';
import { SavedScriptInfo } from '../../../models/saved-script';
import { ParameterRowValue } from '../../parameter-row/parameter-row.component';
import { SubmissionStateService } from '../../../../services/submission-state.service';
import { ProcessService } from '../../../../services/process.service';
import { SavedScriptService } from '../../../../services/saved-script.service';
import { ProcessComponentBase } from '../process.component.base';
import { StorageScriptPickerComponent } from './storage-script-picker/storage-script-picker.component';

/**
 * QuPath script composer - also the base of the ImageJ macro composer (`ImageJMacroComponent`),
 * which differs only in the engine-specific fields above - see jit-ui#138 and jit-parent's
 * doc/Script-Workers.md §8.
 *
 * Submitting this request is a two-phase operation server-side, even though it's a single
 * `POST /process` from here: `SavedScriptService.authorizeAndPrepare` runs static analysis (and
 * an optional LLM review) *before* the Temporal workflow is ever started, so a script that fails
 * review never gets as far as "running" - it comes back as an HTTP error from this same request.
 * `app-submit-button`'s label reflects that ("Scanning…" while this request is in flight); once it
 * resolves, the ordinary status-polling UI every tool already shares takes over and shows the
 * actual run state (SUBMITTED/RUNNING/COMPLETE/ERROR) - no separate "Running…" UI is needed here.
 */
@Component({
  selector: 'qupath-script',
  templateUrl: './qupath-script.component.html',
  styleUrls: ['./qupath-script.component.scss'],
})
export class QupathScriptComponent extends ProcessComponentBase implements OnInit {

  // What differs between the script engines this composer serves. QuPath is the default;
  // ImageJMacroComponent overrides these (see jit-ui#143) and reuses this class and template as is.
  // Plain overridable fields, read only after construction (ngOnInit onward), so a subclass's
  // initializers have always run by then.
  protected engine: ScriptEngine = ScriptEngine.QUPATH_GROOVY;
  protected processType: ProcessType = ProcessType.SCRIPT_QUPATH;
  /** `accept` attribute of the upload button's file input. */
  scriptFileAccept = '.groovy,.txt';
  /** File extensions the storage picker offers; empty means any file. */
  protected scriptFileExtensions: string[] = [];
  scriptPlaceholder = '// Write or paste your QuPath Groovy script here';
  scriptUploadHint = 'Upload a .groovy file from your computer, or write/paste the script directly.';
  parametersHint = 'a script reads whatever keys it defines itself (e.g. params.downsample)';

  get isImageJ(): boolean {
    return this.engine === ScriptEngine.IMAGEJ_MACRO;
  }

  readonly ScriptSourceModeOptions: { label: string; value: ScriptSourceMode; icon: string }[] = [
    { label: 'Use a saved script', value: 'saved', icon: 'pi-bookmark' },
    { label: 'Upload / write a script', value: 'scriptBody', icon: 'pi-upload' },
    { label: 'Choose from storage', value: 'scriptKey', icon: 'pi-folder-open' },
  ];

  savedScripts: SavedScriptInfo[] = [];
  loadingSavedScripts = false;
  // Distinguishes "the request failed" from a successful-but-empty library.
  savedScriptsError = false;

  validating = false;

  // sourceMode/savedScriptId/scriptBody/uploadedFileName/scriptKey/parameterRows are all backed
  // by `form` controls (see configForm) rather than plain fields, purely so persistForm()'s
  // existing snapshot/restore mechanism (ProcessFormStateService) covers them for free - this
  // component gets destroyed and rebuilt every time the user switches tabs or tools (see that
  // service's own javadoc), and a plain field silently resets to its initializer on every rebuild.
  // Confirmed happening for real: switching away and back always landed back on "Upload / write a
  // script" (sourceMode's field-initializer default) regardless of what was last selected. Getters/
  // setters, not formControlName bindings, so every existing [(ngModel)]="sourceMode"-style
  // template binding and plain `this.scriptBody = ...` assignment keeps working unchanged - Angular's
  // two-way ngModel binding and ordinary property assignment both go through the accessor exactly
  // like a plain field, they just happen to read/write the form control underneath.

  get sourceMode(): ScriptSourceMode {
    return this.form.get('sourceMode')?.value ?? 'scriptBody';
  }
  set sourceMode(value: ScriptSourceMode) {
    const changed = this.form.get('sourceMode')?.value !== value;
    this.form.get('sourceMode')?.setValue(value);
    if (changed) this.applySaveAsReusableDefault(value);
  }

  get savedScriptId(): number | null {
    return this.form.get('savedScriptId')?.value ?? null;
  }
  set savedScriptId(value: number | null) {
    this.form.get('savedScriptId')?.setValue(value);
  }

  get scriptBody(): string {
    return this.form.get('scriptBody')?.value ?? '';
  }
  set scriptBody(value: string) {
    this.form.get('scriptBody')?.setValue(value);
  }

  get uploadedFileName(): string | null {
    return this.form.get('uploadedFileName')?.value ?? null;
  }
  set uploadedFileName(value: string | null) {
    this.form.get('uploadedFileName')?.setValue(value);
  }

  get scriptKey(): StorageKey | null {
    return this.form.get('scriptKey')?.value ?? null;
  }
  set scriptKey(value: StorageKey | null) {
    this.form.get('scriptKey')?.setValue(value);
  }

  get parameterRows(): ParameterRowValue[] {
    return this.form.get('parameterRows')?.value ?? [];
  }
  set parameterRows(value: ParameterRowValue[]) {
    this.form.get('parameterRows')?.setValue(value);
  }

  constructor(
    mainFacade: MainFacade,
    @Inject(VISUALIZER) private plotService: IVisualizer,
    private fb: FormBuilder,
    private scriptDialogService: DialogService,
    private processService: ProcessService,
    private savedScriptService: SavedScriptService,
    submissionState: SubmissionStateService,
  ) {
    super('QuPath Script', submissionState, mainFacade);
  }

  ngOnInit(): void {
    this.configForm();

    // These facade streams are singleton subjects that never complete, so tear them down with the
    // component (destroyRef is undefined only outside a DI context, e.g. hand-built in a unit test).
    const selectedNode$ = this.mainFacade.getSelectedNode$();
    const validationMessage$ = this.mainFacade.getValidationMessage$();
    (this.destroyRef ? selectedNode$.pipe(takeUntilDestroyed(this.destroyRef)) : selectedNode$)
      .subscribe({
        next: (node) => this.setSelection(node),
        error: (err) => console.error(`Error with node selection: ${err}`),
      });
    (this.destroyRef
      ? validationMessage$.pipe(takeUntilDestroyed(this.destroyRef))
      : validationMessage$
    ).subscribe({
      next: (message) => {
        this.validationMessage = message;
      },
    });

    this.loadSavedScripts();
  }

  private setSelection(node: TreeNode<IFileInfo> | null): void {
    if (!node?.data) {
      this.bucket = undefined;
      this.path = undefined;
      return;
    }
    this.bucket = node.data.project.bucket;
    this.path = node.leaf ? node.parent?.data?.relPath : node.data?.relPath;
  }

  configForm(): void {
    this.form = this.fb.group({
      sourceMode: ['scriptBody'],
      savedScriptId: [null],
      scriptBody: [''],
      uploadedFileName: [null],
      scriptKey: [null],
      parameterRows: [[]],
      outputFolder: [''],
      editOutputFolder: [false],
      saveAsReusable: [true],
      scriptName: [''],
      scriptDescription: [''],
    });
    this.persistForm();
    // Re-applies only the *disabled* state for whatever mode persistForm() just restored - not
    // the checkbox's value, which persistForm() itself already restored correctly (including a
    // user's own earlier override while in scriptBody/scriptKey mode). Needed because "disabled"
    // isn't part of a FormGroup's .value, so it never round-trips through the snapshot on its own.
    this.updateSaveAsReusableAvailability(this.sourceMode);
  }

  /**
   * "Save this script for reuse" only makes sense for a script you're newly submitting from
   * scratch - reusing an already-saved one has nothing left to save. Resets to that mode's
   * sensible default on every genuine mode *change* (not on every component rebuild - see
   * configForm's call to updateSaveAsReusableAvailability for that case), so the user's own
   * explicit untick within a mode survives a tab-away-and-back, but picking a different mode
   * still gets a clean, predictable default rather than carrying over the previous mode's choice.
   */
  private applySaveAsReusableDefault(mode: ScriptSourceMode): void {
    const control = this.form.get('saveAsReusable');
    if (!control) return;
    if (mode === 'saved') {
      control.setValue(false);
      control.disable();
    } else {
      control.enable();
      control.setValue(true);
    }
  }

  /**
   * Also forces the *value* false for 'saved' mode (not just disabled), unlike
   * applySaveAsReusableDefault's enable/disable-only sibling calls elsewhere: a disabled control
   * is excluded from FormGroup.value entirely, so if the mode was already 'saved' the last time
   * this form's snapshot was taken, that snapshot never had a saveAsReusable key to restore in the
   * first place - patchValue leaves it at its fresh fb.group() default (true) otherwise, which
   * would show as a greyed-out checkbox that's still ticked.
   */
  private updateSaveAsReusableAvailability(mode: ScriptSourceMode): void {
    const control = this.form.get('saveAsReusable');
    if (!control) return;
    if (mode === 'saved') {
      control.setValue(false, { emitEvent: false });
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
  }

  loadSavedScripts(): void {
    this.loadingSavedScripts = true;
    this.savedScriptsError = false;
    this.savedScriptService.list(this.engine).subscribe({
      next: (scripts) => {
        this.savedScripts = scripts;
        this.loadingSavedScripts = false;
      },
      error: () => {
        this.loadingSavedScripts = false;
        this.savedScriptsError = true;
      },
    });
  }

  // ── Script source ──────────────────────────────────────────────────────────

  onUploadScript(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      this.scriptBody = reader.result as string;
      this.uploadedFileName = file.name;
    };
    reader.readAsText(file);
    input.value = '';
  }

  openStoragePicker(): void {
    const ref = this.scriptDialogService.open(StorageScriptPickerComponent, {
      header: 'Choose a script from storage',
      width: '28rem',
      data: { extensions: this.scriptFileExtensions },
    });
    ref.onClose.pipe(take(1)).subscribe((key: StorageKey | undefined) => {
      if (key) this.scriptKey = key;
    });
  }

  get scriptKeyLabel(): string {
    if (!this.scriptKey) return 'No file chosen';
    return `${this.scriptKey.bucket}/${this.scriptKey.object}`;
  }

  // ── Parameters table ─────────────────────────────────────────────────────────

  addParameterRow(): void {
    this.parameterRows = [...this.parameterRows, { key: '', value: '' }];
  }

  removeParameterRow(index: number): void {
    this.parameterRows = this.parameterRows.filter((_, i) => i !== index);
  }

  onParameterRowChange(index: number, row: ParameterRowValue): void {
    const copy = [...this.parameterRows];
    copy[index] = row;
    this.parameterRows = copy;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private parametersAsMap(): { [key: string]: string } {
    const map: { [key: string]: string } = {};
    for (const row of this.parameterRows) {
      if (row.key.trim()) map[row.key.trim()] = row.value;
    }
    return map;
  }

  // ── Annotations ──────────────────────────────────────────────────────────────

  get regionCount(): number {
    return this.plotService.getRegions()?.length ?? 0;
  }

  /** The viewer's current ROIs/annotations as a parsed GeoJSON FeatureCollection, or undefined
   *  if none are drawn - see IVisualizer.getGeoJsonString, same pattern create-rule-dialog uses
   *  for Rule.regions. */
  private currentAnnotations(): unknown {
    if (this.regionCount === 0) return undefined;
    try {
      const geoJson = this.plotService.getGeoJsonString(this.plotService.getRegions());
      return JSON.parse(geoJson);
    } catch {
      return undefined;
    }
  }

  // ── Source validity (for disabling Submit/Validate) ───────────────────────────

  get hasScriptSource(): boolean {
    switch (this.sourceMode) {
      case 'saved':
        return this.savedScriptId != null;
      case 'scriptKey':
        return this.scriptKey != null;
      case 'scriptBody':
      default:
        return !!this.scriptBody.trim();
    }
  }

  // ── Submit / Validate ──────────────────────────────────────────────────────

  private buildRequest() {
    const factory = this.isImageJ ? RequestFactory.getImageJMacroRequest : RequestFactory.getQupathScriptRequest;
    return factory(
      this.form.value,
      this.bucket,
      this.path,
      this.sourceMode,
      this.sourceMode === 'saved' ? this.savedScriptId ?? undefined : undefined,
      this.sourceMode === 'scriptBody' ? this.scriptBody : undefined,
      this.sourceMode === 'scriptKey' ? this.scriptKey ?? undefined : undefined,
      this.parametersAsMap(),
      this.currentAnnotations(),
    );
  }

  onSubmit(): void {
    if (!this.hasScriptSource) return;
    super.submit(this.buildRequest());
  }

  onCreateRule(): void {
    if (!this.hasScriptSource) return;
    super.createRule(this.buildRequest());
  }

  /**
   * Checks the script against JIT's safety review (static analysis + optional LLM pass) without
   * submitting a run - reuses `ProcessService.validate` directly (the same call `runProcess`
   * already makes automatically before every submission) so a rejected script is caught before
   * burning a full image-processing cycle waiting for it to fail.
   */
  onValidateScript(): void {
    if (!this.hasScriptSource) return;
    const instruction = this.mainFacade.createInstruction(this.buildRequest());
    if (!instruction) {
      this.mainFacade.errorMessage('Cannot Validate', 'Select an image or folder first.');
      return;
    }
    this.validating = true;
    this.processService.validate(this.processType, instruction).subscribe({
      next: (validation) => {
        this.validating = false;
        if (validation.type === ValidationType.VALID || validation.type === ValidationType.LIKELY_VALID) {
          this.mainFacade.successMessage('Script Valid', validation.message || 'The script passed JIT\'s safety review.');
        } else {
          this.mainFacade.warningMessage('Script Issue', validation.message);
        }
      },
      error: () => {
        this.validating = false;
        this.mainFacade.errorMessage('Validation Failed', 'Could not validate the script.');
      },
    });
  }
}
